import api from "./api";
import * as db from "./offlineDb";

// Same offline-first pattern as offlineCaptures.js, applied to Tasks (which
// also backs the Schedule module's calendar view - both pages read/write
// through here so they share one offline cache + mutation queue).
const STORE = "tasks";
const CACHE_DAYS = 3;

const makeTempId = () => `offline-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

async function coalesceUpdateMutation(resourceUrl, entityId, payload) {
  const mutations = await db.getMutations();
  const existing = mutations.find((m) => m.entity === "tasks" && m.method === "PATCH" && m.entityId === entityId);
  if (existing) {
    await db.updateMutation({ ...existing, payload: { ...existing.payload, ...payload }, clientTimestamp: Date.now() });
  } else {
    await db.addMutation({ entity: "tasks", method: "PATCH", resourceUrl, entityId, payload, clientTimestamp: Date.now() });
  }
}

export async function listTasks() {
  if (navigator.onLine) {
    try {
      const { data } = await api.get("/tasks");
      const now = Date.now();
      await db.putMany(STORE, data.map((t) => ({ ...t, cachedAt: now })));
      const pendingIds = new Set((await db.getMutations()).filter((m) => m.entity === "tasks").map((m) => m.entityId));
      db.pruneCache(STORE, CACHE_DAYS, pendingIds).catch(() => {});
      return data;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  const cached = await db.getAll(STORE);
  return cached.sort((a, b) => (a.order || 0) - (b.order || 0));
}

export async function createTask(payload) {
  if (navigator.onLine) {
    try {
      const { data } = await api.post("/tasks", payload);
      await db.putOne(STORE, { ...data, cachedAt: Date.now() });
      return data;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  const id = makeTempId();
  const now = new Date().toISOString();
  const localItem = { id, completed: false, order: 0, description: "", ...payload, created_at: now, updated_at: now, offlinePending: true, cachedAt: Date.now() };
  await db.putOne(STORE, localItem);
  await db.addMutation({ entity: "tasks", method: "POST", collectionUrl: "/tasks", tempId: id, entityId: id, payload, clientTimestamp: Date.now() });
  return localItem;
}

export async function updateTask(id, payload) {
  if (navigator.onLine) {
    try {
      const { data } = await api.patch(`/tasks/${id}`, payload);
      await db.putOne(STORE, { ...data, cachedAt: Date.now() });
      return data;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  const existing = (await db.getOne(STORE, id)) || { id };
  const merged = { ...existing, ...payload, updated_at: new Date().toISOString(), offlinePending: true, cachedAt: Date.now() };
  await db.putOne(STORE, merged);
  await coalesceUpdateMutation(`/tasks/${id}`, id, payload);
  return merged;
}

export async function deleteTask(id) {
  if (navigator.onLine) {
    try {
      await api.delete(`/tasks/${id}`);
      await db.deleteOne(STORE, id);
      return;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  await db.deleteOne(STORE, id);
  const mutations = await db.getMutations();
  const related = mutations.filter((m) => m.entity === "tasks" && m.entityId === id);
  const pendingCreate = related.find((m) => m.method === "POST");
  if (pendingCreate) {
    await Promise.all(related.map((m) => db.removeMutation(m.seq)));
    return;
  }
  await Promise.all(related.filter((m) => m.method === "PATCH").map((m) => db.removeMutation(m.seq)));
  await db.addMutation({ entity: "tasks", method: "DELETE", resourceUrl: `/tasks/${id}`, entityId: id, clientTimestamp: Date.now() });
}

export async function reorderTasks(taskIds) {
  if (navigator.onLine) {
    try {
      await api.put("/tasks/reorder", { task_ids: taskIds });
      return;
    } catch (e) {
      if (e.response) throw e;
    }
  }
  const items = await db.getAll(STORE);
  await Promise.all(taskIds.map((id, index) => {
    const item = items.find((t) => t.id === id);
    return item ? db.putOne(STORE, { ...item, order: index, cachedAt: Date.now() }) : null;
  }));
  await db.addMutation({ entity: "tasks", method: "PUT", resourceUrl: "/tasks/reorder", entityId: "__reorder__", payload: { task_ids: taskIds }, clientTimestamp: Date.now() });
}
